import { apiFetch, BASE_URL } from "@/lib/auth-api";

/**
 * Reguły zdarzeniowe device-agenta: backend (core, tick 60 s) ocenia vitals
 * stanowisk aptek WŁASNYCH (W-) i gdy warunek spełniony, wysyła automatyczny
 * toast „raz dziennie do ustania problemu". Konfigurowalna treść ze zmiennymi.
 * camelCase; ścieżki BEZ `/api` (apiFetch dokleja prefix + Bearer).
 */

/**
 * Monitorowana metryka. uptime w DNIACH, dysk C w PROCENTACH zajętości,
 * terminy certyfikatów P1 (bliższy z WSS/TLS) i KOW w DNIACH do wygaśnięcia
 * (próg „zostało ≤ N dni”, z widoku Urządzenia → Poświadczenia).
 */
export type EventMetric = "uptime_days" | "disk_c_used_pct" | "p1_cert_days" | "kow_cert_days";

/**
 * Rodzaj reguły: `metric` = warunek techniczny per urządzenie (vitals + próg),
 * `bloz_change` = szkodliwa zmiana BLOZ na karcie towaru (per apteka+towar,
 * typy utrata/ponowne użycie, zbiorczy dymek raz dziennie).
 */
export type EventRuleKind = "metric" | "bloz_change";

export interface EventRule {
	ruleId: number;
	name: string;
	kind: EventRuleKind;
	/** Tylko `kind === "metric"`; dla `bloz_change` null. */
	metric: EventMetric | null;
	operator: string;
	threshold: number | null;
	/**
	 * Treść toastu z minimalnym markdownem + zmienne: metric → {dni}/{godziny},
	 * {procent} albo dla terminów {dni}/{termin}; bloz_change → {liczba} (lista
	 * kart doklejana automatycznie).
	 */
	messageTemplate: string;
	validityHours: number;
	/** Obecny = reguła ma obrazek; URL podglądu z [`eventRuleImageUrl`]. */
	imageToken: string | null;
	enabled: boolean;
	createdAt: string;
	updatedAt: string;
}

export interface RuleInput {
	name: string;
	/** Pominięty = `metric` (kompatybilność ze starszym panelem). */
	kind?: EventRuleKind;
	metric?: EventMetric;
	threshold?: number;
	messageTemplate: string;
	validityHours?: number;
	enabled?: boolean;
}

/** Incydent reguły bloz: karta z zepsutym BLOZ w konkretnej aptece. */
export interface BlozIncident {
	incidentId: number;
	idfirm: number;
	pharmacyName: string | null;
	idTowr: number;
	nazwa: string | null;
	typ: string;
	fromBloz07: string | null;
	toBloz07: string | null;
	changeDate: string;
	status: "active" | "cleared";
	openedAt: string;
	closedAt: string | null;
	lastNotifiedOn: string | null;
}

/** Incydenty reguły bloz (domyślnie tylko aktywne; `all` = z zamkniętymi). */
export function listBlozIncidents(
	ruleId: number,
	status: "active" | "all" = "active",
): Promise<BlozIncident[]> {
	return apiFetch<BlozIncident[]>(
		`/device-agent/event-rules/${ruleId}/bloz-incidents?status=${status}`,
	);
}

/** Aktywne zdarzenie urządzenia (widok rozwinięty kafla). */
export interface ActiveIncident {
	ruleId: number;
	ruleName: string;
	metric: EventMetric;
	metricValue: number;
	/** Zrenderowana treść — gotowa do pokazania „co jest nie tak". */
	summary: string;
	openedAt: string;
}

export function listEventRules(): Promise<EventRule[]> {
	return apiFetch<EventRule[]>("/device-agent/event-rules");
}

export function createEventRule(input: RuleInput): Promise<EventRule> {
	return apiFetch<EventRule>("/device-agent/event-rules", {
		method: "POST",
		body: JSON.stringify(input),
	});
}

export function updateEventRule(ruleId: number, input: RuleInput): Promise<EventRule> {
	return apiFetch<EventRule>(`/device-agent/event-rules/${ruleId}`, {
		method: "PUT",
		body: JSON.stringify(input),
	});
}

export function deleteEventRule(ruleId: number): Promise<void> {
	return apiFetch<void>(`/device-agent/event-rules/${ruleId}`, { method: "DELETE" });
}

/** Publiczny URL obrazka (ten sam, który załaduje dymek). BASE_URL kończy `/api`. */
export function eventRuleImageUrl(imageToken: string): string {
	return `${BASE_URL}/device-agent/public/event-rule-image/${imageToken}`;
}

export function setEventRuleImage(
	ruleId: number,
	imageBase64: string,
	contentType: string,
): Promise<{ imageToken: string }> {
	return apiFetch<{ imageToken: string }>(`/device-agent/event-rules/${ruleId}/image`, {
		method: "PUT",
		body: JSON.stringify({ imageBase64, contentType }),
	});
}

export function deleteEventRuleImage(ruleId: number): Promise<void> {
	return apiFetch<void>(`/device-agent/event-rules/${ruleId}/image`, { method: "DELETE" });
}

/** Aktywne zdarzenia urządzenia (rozwinięty widok kafla). */
export function listDeviceEventIncidents(deviceUuid: string): Promise<ActiveIncident[]> {
	return apiFetch<ActiveIncident[]>(`/device-agent/devices/${deviceUuid}/event-incidents`);
}

export interface DispatchResult {
	targeted: number;
	deliveredOnline: number;
}

/**
 * Test reguły: wyślij jej treść do urządzeń canary (podgląd zanim reguła odpali).
 * Zmienne podstawiane progiem reguły (np. {procent} → próg %).
 */
export function testEventRuleCanary(ruleId: number): Promise<DispatchResult> {
	return apiFetch<DispatchResult>(`/device-agent/event-rules/${ruleId}/test-canary`, {
		method: "POST",
	});
}
